import { API_BASE } from '../config/api';
import { formatDistance as formatDistanceUtil } from '../utils/distanceFormat';

class DistanceService {
  // Calculate distance and duration between two points
  static async calculateDistance(origin, destination) {
    // TEMPORARY: Skip API calls and use estimated distances for now
    console.log('Using estimated distance calculation (API disabled for cost savings)');
    return this.calculateEstimatedDistance(origin, destination);

    /*
    // FUTURE: Enable this when ready for real distance calculations
    try {
      // Use Google Distance Matrix API through proxy server
      const response = await fetch(`${process.env.REACT_APP_PROXY_URL || `${API_BASE}/api/places`}/distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          origins: [origin], 
          destinations: [destination],
          units: 'imperial' // Miles and minutes
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success' && data.rows && data.rows[0].elements[0].status === 'OK') {
        const element = data.rows[0].elements[0];
        return {
          distance: element.distance,
          duration: element.duration,
          status: 'OK',
          method: 'api'
        };
      } else {
        throw new Error('Distance API returned no results');
      }
    } catch (error) {
      console.warn('Distance API failed, using fallback calculation:', error);
      // Fallback to Haversine formula for straight-line distance
      return this.calculateStraightLineDistance(origin, destination);
    }
    */
  }

  // Calculate estimated distance with realistic driving time estimates
  static calculateEstimatedDistance(origin, destination) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(destination.lat - origin.lat);
    const dLon = this.toRad(destination.lng - origin.lng);
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(this.toRad(origin.lat)) * Math.cos(this.toRad(destination.lat)) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const straightLineDistance = R * c;
    
    // Make driving distance more realistic (add 20-40% for city streets)
    const drivingDistance = straightLineDistance * 1.3; // 30% longer for realistic routing
    
    // Estimate driving time based on distance with realistic city speeds
    let estimatedDriveTime;
    if (drivingDistance <= 1) {
      estimatedDriveTime = drivingDistance * 4; // 15 mph for very short distances (traffic, parking)
    } else if (drivingDistance <= 5) {
      estimatedDriveTime = drivingDistance * 3; // 20 mph for short distances
    } else if (drivingDistance <= 15) {
      estimatedDriveTime = drivingDistance * 2; // 30 mph for medium distances
    } else {
      estimatedDriveTime = drivingDistance * 1.5; // 40 mph for longer distances
    }
    
    return {
      distance: { 
        text: `~${drivingDistance.toFixed(1)} mi`, 
        value: Math.round(drivingDistance * 1609.344) // Convert to meters for consistency
      },
      duration: { 
        text: `~${Math.round(estimatedDriveTime)} min`, 
        value: Math.round(estimatedDriveTime * 60) // Convert to seconds
      },
      status: 'OK',
      method: 'estimated'
    };
  }

  // Fallback: Calculate straight-line distance using Haversine formula
  static calculateStraightLineDistance(origin, destination) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(destination.lat - origin.lat);
    const dLon = this.toRad(destination.lng - origin.lng);
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(this.toRad(origin.lat)) * Math.cos(this.toRad(destination.lat)) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    // Estimate driving time (rough approximation)
    const estimatedDriveTime = distance * 2; // Assume average 30 mph in city
    
    return {
      distance: { 
        text: `${distance.toFixed(1)} mi`, 
        value: Math.round(distance * 1609.344) // Convert to meters for consistency
      },
      duration: { 
        text: `${Math.round(estimatedDriveTime)} min`, 
        value: Math.round(estimatedDriveTime * 60) // Convert to seconds
      },
      status: 'OK',
      method: 'straight_line'
    };
  }

  // Calculate distances for multiple destinations from one origin
  static async calculateDistances(origin, destinations) {
    try {
      // Batch API call for efficiency
      const response = await fetch(`${process.env.REACT_APP_PROXY_URL || `${API_BASE}/api/places`}/distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          origins: [origin], 
          destinations: destinations,
          units: 'imperial'
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success' && data.rows && data.rows[0]) {
        return data.rows[0].elements.map((element, index) => ({
          destination: destinations[index],
          distance: element.distance,
          duration: element.duration,
          status: element.status,
          method: 'api'
        }));
      } else {
        throw new Error('Batch distance API failed');
      }
    } catch (error) {
      console.warn('Batch distance API failed, using individual calculations:', error);
      // Fallback to individual calculations
      const results = [];
      for (const destination of destinations) {
        const result = await this.calculateDistance(origin, destination);
        results.push({
          destination,
          ...result
        });
      }
      return results;
    }
  }

  // Helper function to convert degrees to radians
  static toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  // Filter activities by maximum distance
  static filterByDistance(activities, maxDistanceMiles) {
    if (!maxDistanceMiles) return activities;
    
    const maxDistanceMeters = maxDistanceMiles * 1609.344; // Convert miles to meters
    
    return activities.filter(activity => {
      if (!activity.distance || !activity.distance.distance) return true;
      return activity.distance.distance.value <= maxDistanceMeters;
    });
  }

  // Sort activities by distance
  static sortByDistance(activities, ascending = true) {
    return activities.sort((a, b) => {
      // Put activities without distance at the end
      if (!a.distance && !b.distance) return 0;
      if (!a.distance) return 1;
      if (!b.distance) return -1;
      
      const aDistance = a.distance.distance.value;
      const bDistance = b.distance.distance.value;
      
      return ascending ? aDistance - bDistance : bDistance - aDistance;
    });
  }

  // Get distance category for display purposes
  static getDistanceCategory(distanceValue) {
    if (!distanceValue) return null;
    
    const miles = distanceValue / 1609.344; // Convert meters to miles
    
    if (miles <= 1) return { category: 'very_close', color: 'green', label: 'Very Close' };
    if (miles <= 5) return { category: 'close', color: 'green', label: 'Close' };
    if (miles <= 15) return { category: 'moderate', color: 'yellow', label: 'Moderate' };
    if (miles <= 30) return { category: 'far', color: 'orange', label: 'Far' };
    return { category: 'very_far', color: 'red', label: 'Very Far' };
  }

  // Format distance for display
  static formatDistance(distance, unit = null) {
    if (!distance) return 'Distance unknown';
    // With an explicit unit, always compute from the raw meter value —
    // Google's pre-formatted `text` is in whatever unit Google chose.
    if (unit && distance.value != null) {
      return formatDistanceUtil(distance.value, unit) || 'Distance unknown';
    }
    return distance.text || `${(distance.value / 1609.344).toFixed(1)} mi`;
  }

  // Format duration for display
  static formatDuration(duration) {
    if (!duration) return 'Time unknown';
    
    return duration.text || `${Math.round(duration.value / 60)} min`;
  }

  // Calculate total travel time for a route with multiple stops
  static async calculateRouteTime(startingPoint, activities) {
    if (!activities || activities.length === 0) return null;
    
    let totalTime = 0;
    let currentLocation = startingPoint;
    
    for (const activity of activities) {
      if (!activity.coordinates) continue;
      
      const distance = await this.calculateDistance(currentLocation, activity.coordinates);
      if (distance.status === 'OK') {
        totalTime += distance.duration.value; // Add travel time in seconds
      }
      
      // Update current location for next calculation
      currentLocation = activity.coordinates;
    }
    
    return {
      totalTimeSeconds: totalTime,
      totalTimeText: this.formatDuration({ value: totalTime }),
      averageTimePerStop: activities.length > 0 ? Math.round(totalTime / activities.length / 60) : 0
    };
  }

  // Optimize route order to minimize travel time (simple nearest neighbor algorithm)
  static async optimizeRoute(startingPoint, activities) {
    if (!activities || activities.length <= 1) return activities;
    
    const optimized = [];
    const remaining = [...activities];
    let currentLocation = startingPoint;
    
    while (remaining.length > 0) {
      let nearestIndex = 0;
      let shortestDistance = Infinity;
      
      // Find nearest unvisited activity
      for (let i = 0; i < remaining.length; i++) {
        const activity = remaining[i];
        if (!activity.coordinates) continue;
        
        const distance = await this.calculateDistance(currentLocation, activity.coordinates);
        if (distance.status === 'OK' && distance.distance.value < shortestDistance) {
          shortestDistance = distance.distance.value;
          nearestIndex = i;
        }
      }
      
      // Add nearest activity to optimized route
      const nearestActivity = remaining.splice(nearestIndex, 1)[0];
      optimized.push(nearestActivity);
      currentLocation = nearestActivity.coordinates;
    }
    
    return optimized;
  }

  // Cache distance calculations to avoid repeated API calls
  static cacheKey(origin, destination) {
    return `${origin.lat.toFixed(4)},${origin.lng.toFixed(4)}-${destination.lat.toFixed(4)},${destination.lng.toFixed(4)}`;
  }

  static getCachedDistance(origin, destination) {
    const key = this.cacheKey(origin, destination);
    const cached = sessionStorage.getItem(`distance_${key}`);
    
    if (cached) {
      try {
        const data = JSON.parse(cached);
        // Check if cache is less than 1 hour old
        if (Date.now() - data.timestamp < 3600000) {
          return data.distance;
        }
      } catch (error) {
        console.warn('Error parsing cached distance:', error);
      }
    }
    
    return null;
  }

  static setCachedDistance(origin, destination, distance) {
    const key = this.cacheKey(origin, destination);
    const cacheData = {
      distance,
      timestamp: Date.now()
    };
    
    try {
      sessionStorage.setItem(`distance_${key}`, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error caching distance:', error);
    }
  }

  // Enhanced calculate distance with caching
  static async calculateDistanceWithCache(origin, destination) {
    // Check cache first
    const cached = this.getCachedDistance(origin, destination);
    if (cached) {
      return cached;
    }
    
    // Calculate and cache result
    const distance = await this.calculateDistance(origin, destination);
    this.setCachedDistance(origin, destination, distance);
    
    return distance;
  }
}

export default DistanceService;